"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Sparkles, Snowflake } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Dehradun experiences rapid shifts between weekday corporate/institutional travelers and weekend leisure transit to Mussoorie. We deploy dynamic minimum stay controls and pickup pace-based pricing to prevent panic discounting during shoulder windows.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & length-of-stay fencing"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor intraday rate shifts across MakeMyTrip, Agoda, and Booking.com for Rajpur Road, Sahastradhara, and city-center clusters. Automated alerts protect your RevPAR and capture high-intent travelers before competitors undercut.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized B2B wholesaler leakage and reclaim revenue lost to high OTA commissions. We enforce strict rate parity across metasearch engines while converting transit guests into high-margin direct bookers.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking conversion"
}];
const seasonalDemandCycles = [{
  season: "Summer Peak & Hill Gateway",
  months: "April – June",
  iconName: "Sun",
  focus: "Maximum ADR & Minimum Stay Restrictions",
  description: "High transit leisure volumes traveling to Mussoorie & Garhwal. Premium rate yield and tight inventory controls."
}, {
  season: "Monsoon & Corporate Base",
  months: "July – September",
  iconName: "CloudRain",
  focus: "Corporate Packages & Institutional Base",
  description: "Leisure slowdown offset by corporate accounts, boarding school parent visits, and government institutional demand."
}, {
  season: "Autumn & Wedding Surge",
  months: "October – December",
  iconName: "Sparkles",
  focus: "MICE, Banquets & High-Yield Bundles",
  description: "Heavy wedding banquet demand, festive holiday travel, and pleasant weather driving strong weekend room rates."
}, {
  season: "Winter Transit & Education Influx",
  months: "January – March",
  iconName: "Snowflake",
  focus: "Direct Bookings & Dynamic Weekend Rates",
  description: "Transit ski traffic towards higher altitudes combined with steady educational, defense, and business footfall."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapedehradun" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Dehradun
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Dehradun operates as a distinct micro-market shaped by corporate and educational institutions, destination weddings, and its status as the gateway to Mussoorie and Garhwal. Sunil Singh designs calibrated revenue management strategies built specifically for Dehradun&apos;s city hotels, boutique retreats, and luxury resorts.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-border/70">
            <div>
              <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                Dehradun Demand Calendar
              </span>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-foreground mt-1">
                Seasonal Yield & Rate Strategy Cycles
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Proactive revenue management capitalizes on Dehradun&apos;s quarterly demand shifts before occupancy drops occur.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {seasonalDemandCycles.map((cycle, i) => <div key={cycle.season} data-index={i} className="rounded-lg border border-border/60 bg-muted/30 p-5 flex flex-col justify-between hover:border-secondary/50 transition-[border-color] duration-200">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground">
                      {cycle.iconName === "Sun" && <Sun className="size-4 text-chart-2" />}
                      {cycle.iconName === "CloudRain" && <CloudRain className="size-4 text-chart-2" />}
                      {cycle.iconName === "Sparkles" && <Sparkles className="size-4 text-chart-2" />}
                      {cycle.iconName === "Snowflake" && <Snowflake className="size-4 text-chart-2" />}
                    </div>
                    <Badge variant="outline" className="text-[11px] font-mono border-border">
                      {cycle.months}
                    </Badge>
                  </div>
                  <h4 className="font-semibold text-foreground text-base mb-1">
                    {cycle.season}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {cycle.description}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border/50 text-[11px]">
                  <span className="font-semibold text-secondary block mb-0.5">Tactic:</span>
                  <span className="text-muted-foreground">{cycle.focus}</span>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Dehradun hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}